import React, { useState, useCallback, useEffect } from 'react';
import { Page, Layout, LegacyCard, ResourceList, ResourceItem, Text, Modal, Icon, EmptyState, InlineStack, Badge, Filters, ChoiceList, Button, Banner, Link, Tooltip, BlockStack, Card, Spinner, SkeletonDisplayText } from '@shopify/polaris';
import { ArrowDownIcon, ArrowUpIcon, ClipboardIcon, RefreshIcon, ExternalSmallIcon } from '@shopify/polaris-icons';
import { SkeletonPageCustom } from './SkeletonPageCustom';

import { discountsEmptyStateIconCustom } from "../assets/images";
import { isEmpty, copyToClipboard, defaultInitSettings } from '../Utils';
import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function Campaigns(props) {
    const navigate = useHostNavigate();

    const [loading, setLoading] = useState(true);
    const [initState, setInitState] = useState({});
    const [model, setModel] = useState({});
    const [deleting, setDeleting] = useState(false);
    const [enabling, setEnabling] = useState(false);
    const [disabling, setDisabling] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [appEnabled, setAppEnabled] = useState(true);
    const [enabledLoading, setEnabledLoading] = useState(false);
    const [stateLoading, setStateLoading] = useState(false);
    const [sorting, setSorting] = useState(false);
    const [modalDeleteCampaignsActive, setModalDeleteCampaignsActive] = useState(false);
    const [modalEnableCampaignsActive, setModalEnableCampaignsActive] = useState(false);
    const [modalDisableCampaignsActive, setModalDisableCampaignsActive] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [redirectUrl, setRedirectUrl] = useState('');

    const [filter, setFilter] = useState(() => {
        let campaignMethod = '';
        let campaignStatus = '';

        const domain = props.user?.myShopifyDomain;

        if (domain) {
            campaignMethod = localStorage.getItem(`${domain}_campaigns_campaignMethod`);
            if (!campaignMethod) {
                campaignMethod = '';
            }

            campaignStatus = localStorage.getItem(`${domain}_campaigns_campaignStatus`);
            if (!campaignStatus) {
                campaignStatus = '';
            }
        }

        return { queryValue: '', campaignStatus: campaignStatus ? [campaignStatus] : [], campaignMethod: campaignMethod ? [campaignMethod] : [] };
    });

    const [initSettings, setInitSettings] = useState(() => {
        const domain = props.user?.myShopifyDomain;

        if (!domain) {
            return defaultInitSettings;
        }

        const stored = localStorage.getItem(`${domain}_initSettings`);

        return JSON.parse(stored || "null") || defaultInitSettings;
    });

    const fetch = FetchCustom();

    const filterCampaigns = function (items) {
        return items.filter(p => (isEmpty(filter.campaignMethod) || (filter.campaignMethod[0] === 'Automatic' && (p.campaignMethodId === 1 || p.campaignMethodId === 4)) || (filter.campaignMethod[0] === 'Code' && p.campaignMethodId === 2) || (filter.campaignMethod[0] === 'Bulk codes' && p.campaignMethodId === 3))
            && (isEmpty(filter.campaignStatus) || p.statusName === filter.campaignStatus[0])
            && (isEmpty(filter.queryValue) || p.displayName.toLowerCase().indexOf(filter.queryValue.toLowerCase()) > -1 || p.displayDescription && p.displayDescription.toLowerCase().indexOf(filter.queryValue.toLowerCase()) > -1)); //eslint-disable-line
    }

    const populateData = async () => {

        props.setIsLoading(true);
        const data = await fetch('api/home/campaigns');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setInitState(data);

            const model = {
                ...data,
                campaigns: filterCampaigns(data.campaigns)
            };

            setModel(model);
            setLoading(false);
        }
    };

    const getMainThemeSettingsData = async () => {
        const data = await fetch('api/home/getMainThemeSettingsData');

        setEnabledLoading(false);
        setStateLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setAppEnabled(!data.coreEmbedBlockDisabled);
            updateInitSetting('appEnabled', !data.coreEmbedBlockDisabled);
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    const updateInitSetting = (keyName, value) => {
        if (!props.user?.myShopifyDomain) return;

        const key = `${props.user.myShopifyDomain}_initSettings`;

        setInitSettings(current => {
            const updated = {
                ...current,
                [keyName]: value,
            };

            localStorage.setItem(key, JSON.stringify(updated));

            return updated;
        });
    };

    const sortCampaigns = async (fromSeq, toSeq) => {
        setSorting(true);
        const data = await fetch('api/home/sortCampaigns', { fromSeq: fromSeq, toSeq: toSeq });

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setInitState((prevState) => {
                let initState = Object.assign({}, prevState);
                initState['campaigns'] = data;
                return initState;
            });

            setSorting(false);
        }
    };

    const orderedCampaigns = function () {
        if (loading) {
            return Array.from({ length: initSettings.campaignsCount }, () => ({}));
        }
        else {
            if (!model.campaigns) {
                return [];
            }

            return model.campaigns.sort(function (a, b) {
                return a.seq < b.seq ? -1 : (a.seq > b.seq ? 1 : 0);
            });
        }
    }

    const applyFilters = function () {
        let filteredItems = filterCampaigns(initState.campaigns);
        updateInitSetting('campaignsCount', filteredItems.length);

        setModel((prevState) => {
            let model = Object.assign({}, prevState);
            model['campaigns'] = filteredItems;
            return model;
        });
        setSelectedItems([]);
    }

    const handleSelectItems = useCallback(
        (newValue) => setSelectedItems(newValue),
        [],
    );

    const handleSeqChange = useCallback(
        (e, id, index, up) => {
            e.stopPropagation();

            if (props.user.viewOnly) return;

            var campaigns = model.campaigns;
            var item = campaigns[index];

            var swapIndex = up ? index - 1 : index + 1;
            if (swapIndex > -1 && swapIndex < campaigns.length) {
                var itemSwap = campaigns[swapIndex];
                sortCampaigns(item.seq, itemSwap.seq);
            }
        },
        // eslint-disable-next-line
        [model, sorting],
    );

    const handleFilterChange = useCallback(
        (filterName, newValue) => {
            setFilter((prevState) => {
                let filter = Object.assign({}, prevState);
                filter[filterName] = newValue;
                return filter;
            });

            if (filterName === 'campaignMethod' || filterName === 'campaignStatus') {
                localStorage.setItem(`${props.user.myShopifyDomain}_campaigns_${filterName}`, newValue);
            }
        },
        [props.user.myShopifyDomain],
    );

    const handleClearFilter = useCallback(
        (filterName) => {
            setFilter((prevState) => {
                let filter = Object.assign({}, prevState);
                filter[filterName] = [];
                return filter;
            });

            if (filterName === 'campaignMethod' || filterName === 'campaignStatus') {
                localStorage.setItem(`${props.user.myShopifyDomain}_campaigns_${filterName}`, '');
            }
        },
        [props.user.myShopifyDomain],
    );

    const handleFilterClearAll = useCallback(
        () => {
            setFilter((prevState) => {
                let filter = Object.assign({}, prevState);
                filter.campaignMethod = [];
                filter.campaignStatus = [];
                filter.queryValue = '';
                return filter;
            });

            localStorage.setItem(`${props.user.myShopifyDomain}_campaigns_campaignMethod`, '');
            localStorage.setItem(`${props.user.myShopifyDomain}_campaigns_campaignStatus`, '');
        },
        [props.user.myShopifyDomain],
    );

    const toggleModalDeleteCampaigns = useCallback(
        () => setModalDeleteCampaignsActive(!modalDeleteCampaignsActive),
        [modalDeleteCampaignsActive],
    );

    const toggleModalEnableCampaigns = useCallback(
        () => setModalEnableCampaignsActive(!modalEnableCampaignsActive),
        [modalEnableCampaignsActive],
    );

    const toggleModalDisableCampaigns = useCallback(
        () => setModalDisableCampaignsActive(!modalDisableCampaignsActive),
        [modalDisableCampaignsActive],
    );

    const enableCampaigns = async () => {
        setEnabling(true);
        props.setIsLoading(true);

        const data = await fetch('api/home/enableCampaigns', selectedItems);

        setEnabling(false);
        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            var message = "Campaign activated";
            if (selectedItems.length > 1) {
                message = selectedItems.length + " campaigns activated";
            }

            var items = model.campaigns;
            var enabledItems = items.filter((option) => selectedItems.indexOf(option.id) > -1);

            enabledItems.forEach(function (item, index) {
                item.statusName = 'Active';
            });

            setSelectedItems([]);

            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model['campaigns'] = items;
                return model;
            });

            setInitState((prevState) => {
                let initState = Object.assign({}, prevState);
                initState['campaigns'] = items;
                return initState;
            });

            toggleModalEnableCampaigns();
            props.changeToastActive(true, message);
        }
    };

    const disableCampaigns = async () => {
        setDisabling(true);
        props.setIsLoading(true);

        const data = await fetch('api/home/disableCampaigns', selectedItems);

        setDisabling(false);
        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            var message = "Campaign deactivated";
            if (selectedItems.length > 1) {
                message = selectedItems.length + " campaigns deactivated";
            }

            var items = model.campaigns;
            var disabledItems = items.filter((option) => selectedItems.indexOf(option.id) > -1);

            disabledItems.forEach(function (item, index) {
                item.statusName = 'Expired';
            });

            setSelectedItems([]);

            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model['campaigns'] = items;
                return model;
            });

            setInitState((prevState) => {
                let initState = Object.assign({}, prevState);
                initState['campaigns'] = items;
                return initState;
            });

            toggleModalDisableCampaigns();
            props.changeToastActive(true, message);
        }
    };

    const deleteCampaigns = async () => {
        setDeleting(true);
        props.setIsLoading(true);

        const data = await fetch('api/home/deleteCampaigns', selectedItems);

        setDeleting(false);
        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            var message = "Campaign deleted";
            if (selectedItems.length > 1) {
                message = selectedItems.length + " campaigns deleted";
            }

            var items = model.campaigns.filter((option) => selectedItems.indexOf(option.id) === -1);

            setSelectedItems([]);

            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model['campaigns'] = items;
                return model;
            });

            setInitState((prevState) => {
                let initState = Object.assign({}, prevState);
                initState['campaigns'] = items;
                return initState;
            });

            toggleModalDeleteCampaigns();
            props.changeToastActive(true, message);
        }
    };

    useEffect(() => {
        getMainThemeSettingsData();
        getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (JSON.stringify(props.user) !== '{}') {
            document.title = "Campaigns | Stack Discounts";
            populateData();
        }
        // eslint-disable-next-line
    }, [props.user]);

    useEffect(() => {
        if (JSON.stringify(initState) !== '{}') {
            applyFilters();
        }
        // eslint-disable-next-line
    }, [initState, filter]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    const modalDeleteCampaigns = (
        <Modal
            title={"Delete " + (selectedItems.length > 1 ? selectedItems.length + " " : "") + "campaign" + (selectedItems.length > 1 ? "s" : "") + "?"}
            open={modalDeleteCampaignsActive}
            onClose={toggleModalDeleteCampaigns}
            primaryAction={{
                content: 'Delete',
                onAction: () => { deleteCampaigns() },
                destructive: true,
                loading: deleting
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: toggleModalDeleteCampaigns
                },
            ]}
        >
            <div id="modal-delete-campaigns">
                <Modal.Section>
                    This can't be undone.
                </Modal.Section>
            </div>
        </Modal>
    );

    const modalEnableCampaigns = (
        <Modal
            title={'Activate selected campaigns?'}
            open={modalEnableCampaignsActive}
            onClose={toggleModalEnableCampaigns}
            primaryAction={{
                content: 'Activate',
                onAction: () => { enableCampaigns() },
                loading: enabling
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: toggleModalEnableCampaigns
                },
            ]}
        >
            <div id="modal-enable-campaigns">
                <Modal.Section>
                    These campaigns will become active now.
                </Modal.Section>
            </div>
        </Modal>
    );

    const modalDisableCampaigns = (
        <Modal
            title={'Deactivate selected campaigns?'}
            open={modalDisableCampaignsActive}
            onClose={toggleModalDisableCampaigns}
            primaryAction={{
                content: 'Deactivate',
                onAction: () => { disableCampaigns() },
                loading: disabling
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: toggleModalDisableCampaigns
                },
            ]}
        >
            <div id="modal-disable-campaigns">
                <Modal.Section>
                    These campaigns will expire now.
                </Modal.Section>
            </div>
        </Modal>
    );

    const filters = [
        {
            key: 'campaignMethod',
            label: 'Method',
            filter: (
                <ChoiceList
                    choices={[
                        { label: 'Automatic', value: 'Automatic' },
                        { label: 'Code', value: 'Code' },
                        { label: 'Bulk codes', value: 'Bulk codes' },
                    ]}
                    selected={filter.campaignMethod}
                    onChange={(newValue) => handleFilterChange('campaignMethod', newValue)}
                />
            ),
            shortcut: true,
        },
        {
            key: 'campaignStatus',
            label: 'Status',
            filter: (
                <ChoiceList
                    choices={[
                        { label: 'Active', value: 'Active' },
                        { label: 'Scheduled', value: 'Scheduled' },
                        { label: 'Expired', value: 'Expired' },
                    ]}
                    selected={filter.campaignStatus}
                    onChange={(newValue) => handleFilterChange('campaignStatus', newValue)}
                />
            ),
            shortcut: true,
        }
    ];

    const appliedFilters = [];
    if (!isEmpty(filter.campaignMethod)) {
        const key = 'campaignMethod';
        appliedFilters.push({
            key,
            label: 'Method is ' + filter.campaignMethod[0],
            onRemove: () => { handleClearFilter('campaignMethod') }
        });
    }
    if (!isEmpty(filter.campaignStatus)) {
        const key = 'campaignStatus';
        appliedFilters.push({
            key,
            label: 'Status is ' + filter.campaignStatus[0],
            onRemove: () => { handleClearFilter('campaignStatus') }
        });
    }

    return (
        <Page title="Campaigns" primaryAction={props.user.myShopifyDomain !== 'mandalaroseworks.myshopify.com' && props.user.myShopifyDomain !== 'helperlo24.myshopify.com' && props.user.myShopifyDomain !== 'nnd-test.myshopify.com' && props.user.myShopifyDomain !== 'discounts-07.myshopify.com' ? { content: 'Create campaign', disabled: model.isFreePlan && model.campaigns.length >= 1, onAction: () => { navigate('/campaigns/new') } } : null}
            actionGroups={props.user.myShopifyDomain === 'mandalaroseworks.myshopify.com' || props.user.myShopifyDomain === 'helperlo24.myshopify.com' || props.user.myShopifyDomain === 'nnd-test.myshopify.com' || props.user.myShopifyDomain === 'discounts-07.myshopify.com' ? [
                {
                    title: 'Create campaign',
                    actions: [
                        {
                            content: 'General campaign',
                            onAction: () => { navigate('/campaigns/new') },
                        },
                        {
                            content: 'Special campaign',
                            onAction: () => { navigate('/campaigns/new/5') },
                        },
                    ],
                },
            ] : []}
        >
            {modalDeleteCampaigns}
            {modalEnableCampaigns}
            {modalDisableCampaigns}
            <Layout>
                {
                    !initSettings.appEnabled || !appEnabled ?
                        <Layout.Section>
                            <div className="card-main">
                                <Card>
                                    <BlockStack gap="300">
                                        <div className="qs-title">
                                            <Text as="h2" variant="headingMd">
                                                Enabling required!
                                            </Text>
                                        </div>
                                        <Banner tone={enabledLoading ? 'warning' : 'critical'}>
                                            <InlineStack align="space-between" blockAlign="center" gap="200">
                                                <BlockStack>
                                                    <Text as="p">
                                                        {
                                                            enabledLoading ?
                                                                'Recheck state button will update the current state of the app.' :
                                                                'This step is required only for syncing with your storefront.'
                                                        }
                                                    </Text>
                                                    {
                                                        !enabledLoading ?
                                                            <Text as="p">
                                                                It will not affect your theme's visuals. <a href="https://uniocode.gitbook.io/stack-discounts/1.-getting-started" rel="noreferrer" target="_blank" className="Polaris-Link Polaris-Link--monochrome ml-5">Learn more</a>
                                                            </Text>
                                                            : null
                                                    }
                                                </BlockStack>
                                                {
                                                    enabledLoading ?
                                                        <Button
                                                            onClick={() => {
                                                                if (props.user.viewOnly) return;

                                                                setStateLoading(true);
                                                                getMainThemeSettingsData();
                                                            }}
                                                            loading={stateLoading}
                                                            variant="secondary"
                                                        >Recheck state</Button>
                                                        :
                                                        <Button
                                                            onClick={() => {
                                                                if (props.user.viewOnly) return;

                                                                setEnabledLoading(true);
                                                                window.open(props.user.extensionDeepLink, '_blank');
                                                            }}
                                                            icon={ExternalSmallIcon}
                                                            variant="primary"
                                                        >Enable app</Button>
                                                }
                                            </InlineStack>
                                        </Banner>
                                    </BlockStack>
                                </Card>
                            </div>
                        </Layout.Section>
                        : null
                }
                {
                    model.isFreePlan ?
                        <Layout.Section>
                            <Banner
                                title="Free plan limitations"
                                status="warning"
                            >
                                <p>You can have a maximum of 2 campaigns with 2 discounts assigned.</p>
                                <p>You can always upgrade to a paid plan on the <Link url="/settings/billing">Billing</Link> page and avoid all limitations.</p>
                            </Banner>
                        </Layout.Section>
                        : null
                }
                {
                    model.isFreePlan && model.campaigns.length > 1 ?
                        <Layout.Section>
                            <Banner
                                title="Free plan limitations exceeded."
                                status="critical"
                            >
                                <p>Free plan limitations are not met. You need to remove some campaigns or upgrade to paid plan on the <Link monochrome url="/settings/billing">Billing</Link> page.</p>
                                <p>Only after that, you can enable the app. See about limitations at the top of this page.</p>
                            </Banner>
                        </Layout.Section>
                        : null
                }
                <Layout.Section>
                    <LegacyCard>
                        <div id="campaigns">
                            <ResourceList
                                emptyState={
                                    <EmptyState
                                        heading="Create and manage your campaigns"
                                        action={{ content: 'Create campaign', onAction: () => { navigate('/campaigns/new') } }}
                                        image={discountsEmptyStateIconCustom}
                                    >
                                        <p>This is where you will add and manage your campaigns.</p>
                                    </EmptyState>
                                }
                                resourceName={{ singular: 'campaign', plural: 'campaigns' }}
                                loading={sorting}
                                items={orderedCampaigns()}
                                renderItem={(item, id, index) => {
                                    const { displayName } = item;
                                    const count = loading ? initSettings.campaignsCount : model.campaigns.length;
                                    return (
                                        <ResourceItem
                                            id={id}
                                            accessibilityLabel={`View details for ${displayName}`}
                                            name={displayName}
                                            onClick={loading ? undefined : () => navigate('/campaigns/' + id)}
                                        >
                                            {
                                                !loading ?
                                                    <InlineStack wrap={false}>
                                                        <div>
                                                            <div className="order-button">
                                                                <Button variant="plain" disabled={index === 0} onClick={(e) => handleSeqChange(e, id, index, true)}><Icon source={ArrowUpIcon} /></Button>
                                                            </div>
                                                            <div className="order-button">
                                                                <Button className="order-button" variant="plain" disabled={index === count - 1} onClick={(e) => handleSeqChange(e, id, index, false)}><Icon source={ArrowDownIcon} /></Button>
                                                            </div>
                                                        </div>
                                                        <div className="w-100">
                                                            <BlockStack gap="100">
                                                                <Text variant="bodyMd" fontWeight="semibold" as="h3">{displayName}</Text>
                                                                <InlineStack gap="100" wrap={false} blockAlign="center">
                                                                    <Text as="span" tone="subdued">
                                                                        {item.method + (item.campaignMethodId === 2 || item.campaignMethodId === 3 ? ':' : '')}
                                                                    </Text>
                                                                    {
                                                                        item.campaignMethodId === 2 ?
                                                                            <InlineStack gap="200" blockAlign="center">
                                                                                <Text as="span">{item.code}</Text>
                                                                                <div className="resource-item-copy-code display-flex-span">
                                                                                    <Tooltip content="Copy code">
                                                                                        <Button disabled={!item.code} variant="tertiary" icon={ClipboardIcon} onClick={(e) => copyToClipboard(item.code ?? '', e)} />
                                                                                    </Tooltip>
                                                                                </div>
                                                                            </InlineStack>
                                                                            : null
                                                                    }
                                                                    {
                                                                        item.campaignMethodId === 3 ?
                                                                            <Text as="span">{item.codeCount}</Text>
                                                                            : null
                                                                    }
                                                                </InlineStack>
                                                            </BlockStack>
                                                        </div>
                                                        <div>
                                                            <div className="campaign-list-status"><Text as="span"><Badge tone={item.statusName === "Active" ? "success" : (item.statusName === "Scheduled" ? "attention" : "default")}>{item.statusName}</Badge></Text></div>
                                                            <div className="campaign-list-subtext"><span className="no-wrap">{item.discountsCount + ' discount' + (item.discountsCount > 1 ? 's' : '')}</span></div>
                                                        </div>
                                                    </InlineStack>
                                                    :
                                                    <InlineStack wrap={false}>
                                                        <div>
                                                            <div className="order-button">
                                                                <Button variant="plain" disabled={index === 0}><Icon source={ArrowUpIcon} /></Button>
                                                            </div>
                                                            <div className="order-button">
                                                                <Button className="order-button" variant="plain" disabled={index === count - 1}><Icon source={ArrowDownIcon} /></Button>
                                                            </div>
                                                        </div>
                                                        <div className="w-100">
                                                            <BlockStack gap="100">
                                                                <Text variant="bodyMd" fontWeight="semibold" as="h3">
                                                                    <div className="campaigns-skeleton-20 title">
                                                                        <SkeletonDisplayText size="small" />
                                                                    </div>
                                                                </Text>
                                                                <div className="campaigns-skeleton-20">
                                                                    <SkeletonDisplayText size="small" />
                                                                </div>
                                                            </BlockStack>
                                                        </div>
                                                        <div>
                                                            <div className="campaign-list-status">
                                                                <div className="campaigns-skeleton-20 status">
                                                                    <Text as="span"><Badge tone="default"></Badge></Text>
                                                                </div>
                                                            </div>
                                                            <div className="campaign-list-subtext">
                                                                <div className="campaigns-skeleton-20 discounts">
                                                                    <SkeletonDisplayText size="small" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </InlineStack>
                                            }
                                        </ResourceItem>
                                    );
                                }}
                                filterControl={
                                    <Filters
                                        queryValue={filter.queryValue}
                                        filters={filters}
                                        queryPlaceholder="Search campaigns"
                                        appliedFilters={appliedFilters}
                                        onQueryChange={(newValue) => handleFilterChange('queryValue', newValue)}
                                        onQueryClear={() => handleFilterChange('queryValue', '')}
                                        onClearAll={() => handleFilterClearAll()}
                                    >
                                    </Filters>
                                }
                                selectedItems={selectedItems}
                                onSelectionChange={(newValue) => handleSelectItems(newValue)}
                                bulkActions={[
                                    {
                                        content: 'Activate campaigns',
                                        onAction: () => {
                                            if (props.user.viewOnly) return;
                                            toggleModalEnableCampaigns();
                                        },
                                    },
                                    {
                                        content: 'Deactivate campaigns',
                                        onAction: () => {
                                            if (props.user.viewOnly) return;
                                            toggleModalDisableCampaigns();
                                        },
                                    },
                                    {
                                        destructive: true,
                                        content: 'Delete campaigns',
                                        onAction: () => {
                                            if (props.user.viewOnly) return;
                                            toggleModalDeleteCampaigns();
                                        },
                                    }
                                ]}
                            />
                        </div>
                    </LegacyCard>
                </Layout.Section>
            </Layout>
        </Page >
    );
}